import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Videocam as CameraGlyphIcon } from '@mui/icons-material';
import type {
  CameraFacetItem,
  CameraLocationsOverlay,
  CameraMarkerSize,
} from '../hooks/useCameraLocationsOverlay';
import {
  CAMERA_NEAR_TRACK_RADII_M,
  type CameraNearTrackRadius,
  type CameraNearTracksPrefs,
} from '../services/cameraProximity';

/** Optional "only cameras near the loaded tracks" control (Pathfinder). */
export type CameraProximityControl = {
  prefs: CameraNearTracksPrefs;
  onChange: (patch: Partial<CameraNearTracksPrefs>) => void;
  /** Number of track points currently loaded (0 → filter is moot). */
  trackPointCount: number;
  /** Cameras that survived the proximity filter. */
  matchedCount: number;
  /** Copy overrides; defaults describe Pathfinder's historical tracks. */
  labels?: {
    /** Switch label, e.g. "Near entities". */
    title?: string;
    /** Caption when nothing to be near, e.g. "No live entities — all cameras shown." */
    empty?: string;
    /** Plural noun for the point count, e.g. "live entities". */
    noun?: string;
  };
};

export type CameraLayerFilterOverlay = Pick<
  CameraLocationsOverlay,
  | 'facets'
  | 'featureCount'
  | 'totalCount'
  | 'hasActiveFilters'
  | 'setCategoryVisible'
  | 'setSourceVisible'
  | 'soloCategory'
  | 'soloSource'
  | 'setAllCategoriesVisible'
  | 'setAllSourcesVisible'
  | 'clearFilters'
  | 'display'
  | 'setDisplay'
>;

type PanelProps = {
  overlay: CameraLayerFilterOverlay;
  /** Rendered at the top of the panel unless the host places `CameraProximityRow` itself. */
  proximity?: CameraProximityControl;
  /** Hide the header row ("Camera layers · n / m shown"); the host shows its own. */
  hideHeader?: boolean;
  /** Hide the cluster / labels / size row (the host renders those switches itself). */
  hideDisplay?: boolean;
};

/**
 * "Only cameras within N m of the tracks / live entities" switch + radius
 * chips. Exported so the map-layer dialogs can show it inline under CAMERAS,
 * where operators look for it, instead of inside the collapsed filter panel.
 */
export const CameraProximityRow: React.FC<{ proximity: CameraProximityControl; disabled?: boolean }> = ({
  proximity,
  disabled,
}) => (
  <Box>
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={0.5}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            size="small"
            checked={proximity.prefs.enabled}
            disabled={disabled}
            onChange={(_, v) => proximity.onChange({ enabled: v })}
          />
        }
        label={
          <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {proximity.labels?.title ?? 'Near tracks'}
          </Typography>
        }
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        disabled={disabled || !proximity.prefs.enabled}
        value={proximity.prefs.radiusM}
        onChange={(_, v: CameraNearTrackRadius | null) => v && proximity.onChange({ radiusM: v })}
        sx={{ '& .MuiToggleButton-root': { px: 0.75, py: 0.2, fontSize: '0.62rem', textTransform: 'none', whiteSpace: 'nowrap' } }}
        aria-label="Distance from track"
      >
        {CAMERA_NEAR_TRACK_RADII_M.map((r) => (
          <ToggleButton key={r} value={r}>
            {`${r} m`}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {!proximity.prefs.enabled
          ? 'All cameras shown.'
          : proximity.trackPointCount === 0
            ? (proximity.labels?.empty ?? 'No track loaded — all cameras shown.')
            : `${proximity.matchedCount.toLocaleString()} within ${proximity.prefs.radiusM} m of ${proximity.trackPointCount.toLocaleString()} ${proximity.labels?.noun ?? 'track points'}.`}
      </Typography>
      {proximity.prefs.enabled && proximity.trackPointCount > 0 && proximity.matchedCount === 0 && (
        <Button
          size="small"
          onClick={() => proximity.onChange({ enabled: false })}
          sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.66rem', textTransform: 'none', lineHeight: 1.3 }}
        >
          Show all
        </Button>
      )}
    </Stack>
  </Box>
);

type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  proximity?: CameraProximityControl;
  overlay: CameraLayerFilterOverlay;
};

type SectionProps = {
  title: string;
  items: CameraFacetItem[];
  showSwatch: boolean;
  onToggle: (key: string, visible?: boolean) => void;
  onSolo: (key: string) => void;
  onAll: (visible: boolean) => void;
};

const FacetSection: React.FC<SectionProps> = ({ title, items, showSwatch, onToggle, onSolo, onAll }) => {
  const visibleCount = items.filter((i) => i.visible).length;
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <Typography variant="overline" sx={{ lineHeight: 1.6 }}>
          {title}{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            ({visibleCount}/{items.length})
          </Typography>
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button size="small" sx={{ minWidth: 0, px: 0.75, fontSize: '0.7rem' }} onClick={() => onAll(true)}>
            All
          </Button>
          <Button size="small" sx={{ minWidth: 0, px: 0.75, fontSize: '0.7rem' }} onClick={() => onAll(false)}>
            None
          </Button>
        </Stack>
      </Stack>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Nothing loaded yet.
        </Typography>
      ) : (
        <Stack spacing={0}>
          {items.map((item) => (
            <Stack
              key={item.key}
              direction="row"
              alignItems="center"
              sx={{
                '&:hover .camera-facet-only': { opacity: 1 },
                opacity: item.visible ? 1 : 0.6,
                borderRadius: 0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Checkbox
                size="small"
                checked={item.visible}
                onChange={(_, checked) => onToggle(item.key, checked)}
                sx={{ p: 0.5 }}
                inputProps={{ 'aria-label': `${item.visible ? 'Hide' : 'Show'} ${item.label}` }}
              />
              {showSwatch && (
                <CameraGlyphIcon
                  sx={{ fontSize: 15, color: item.color, mr: 0.75, flexShrink: 0, filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.9))' }}
                />
              )}
              <Typography
                variant="body2"
                noWrap
                title={item.label}
                sx={{ flex: 1, cursor: 'pointer', fontSize: '0.8rem' }}
                onClick={() => onToggle(item.key)}
              >
                {item.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mx: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                {item.count.toLocaleString()}
              </Typography>
              <Tooltip title={`Show only ${item.label}`} placement="right">
                <Button
                  className="camera-facet-only"
                  size="small"
                  onClick={() => onSolo(item.key)}
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.65rem', opacity: 0, transition: 'opacity 120ms' }}
                >
                  only
                </Button>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Camera filter controls (proximity, display, category, source) without the
 * popover chrome, so a host dialog (COP "Map layers") can embed them inline.
 */
export const CameraLayerFilterPanel: React.FC<PanelProps> = ({ overlay, proximity, hideHeader, hideDisplay }) => {
  const { facets, featureCount, totalCount, hasActiveFilters } = overlay;
  const shownCount = proximity && proximity.prefs.enabled && proximity.trackPointCount > 0
    ? proximity.matchedCount
    : featureCount;
  return (
    <>
      {!hideHeader && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">Camera layers</Typography>
            <Typography variant="caption" color="text.secondary">
              {shownCount.toLocaleString()} / {totalCount.toLocaleString()} shown
            </Typography>
          </Stack>
          <Divider sx={{ mb: 1 }} />
        </>
      )}
      <Stack spacing={1.25}>
        {proximity && (
          <>
            <CameraProximityRow proximity={proximity} />
            <Divider />
          </>
        )}
        {/* Display: cluster / labels / marker size (persisted, shared by both maps) */}
        {!hideDisplay && (
        <Box>
          <Typography variant="overline" sx={{ lineHeight: 1.6 }}>
            Display
          </Typography>
          <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ columnGap: 1 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={overlay.display.cluster}
                  onChange={(_, v) => overlay.setDisplay({ cluster: v })}
                />
              }
              label={<Typography variant="caption">Cluster</Typography>}
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={overlay.display.labels}
                  onChange={(_, v) => overlay.setDisplay({ labels: v })}
                />
              }
              label={<Typography variant="caption">Labels</Typography>}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={overlay.display.size}
              onChange={(_, v: CameraMarkerSize | null) => v && overlay.setDisplay({ size: v })}
              sx={{ ml: 'auto', '& .MuiToggleButton-root': { px: 0.75, py: 0.25, fontSize: '0.65rem', textTransform: 'none' } }}
              aria-label="Camera marker size"
            >
              <ToggleButton value="small">S</ToggleButton>
              <ToggleButton value="medium">M</ToggleButton>
              <ToggleButton value="large">L</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>
        )}
        {!hideDisplay && <Divider />}
        <FacetSection
          title="Category"
          items={facets.categories}
          showSwatch
          onToggle={overlay.setCategoryVisible}
          onSolo={overlay.soloCategory}
          onAll={overlay.setAllCategoriesVisible}
        />
        <Divider />
        <FacetSection
          title="Source"
          items={facets.sources}
          showSwatch={false}
          onToggle={overlay.setSourceVisible}
          onSolo={overlay.soloSource}
          onAll={overlay.setAllSourcesVisible}
        />
      </Stack>
      {hasActiveFilters && (
        <>
          <Divider sx={{ my: 1 }} />
          <Button size="small" fullWidth variant="outlined" onClick={overlay.clearFilters}>
            Reset — show all cameras
          </Button>
        </>
      )}
    </>
  );
};

/**
 * Map-tool popover for toggling camera layers on/off by category and source.
 * Used by the Pathfinder toolbar; COP embeds `CameraLayerFilterPanel` in its
 * Map layers dialog. State lives in `useCameraLocationsOverlay` so both maps
 * see the same choices.
 */
const CameraLayerFilterPopover: React.FC<Props> = ({ anchorEl, open, onClose, overlay, proximity }) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
    transformOrigin={{ vertical: 'center', horizontal: 'right' }}
    PaperProps={{ sx: { p: 1.5, width: 300, maxHeight: 460, overflowY: 'auto' } }}
  >
    <CameraLayerFilterPanel overlay={overlay} proximity={proximity} />
  </Popover>
);

export default CameraLayerFilterPopover;
